import { useRef } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormulaField } from "@/components/formula/formula-field";
import { cn } from "@/lib/utils";
import { FieldDataType } from "@/types/dynamic-detail";
import { KeyValueRow, keyValueRows } from "@/utils/dynamic-form";

/** A row's single-line boxes, the height of the first line of a template value beside them. */
const ROW_INPUT = "h-[30px] rounded-sm bg-white px-2 shadow-sm";

type KeyValueFieldProps = {
    field: FieldDataType;
    form: UseFormReturn<FieldValues>;
    /** Form path of the field, which holds the rows (`KeyValueRow[]`). */
    name: string;
    label: string;
    description?: string;
    disabled?: boolean;
};

/**
 * A free mapping of names to values, one row per entry.
 *
 * What a `key_value` field gets instead of a JSON textarea: nobody has to balance braces or
 * quote a name to change one value. When the descriptor declares a `formula`, each value is
 * that formula's input — for a map of templates, a one-line template box where `{{` opens
 * the variables and a click opens the full editor — so the field is checked and completed
 * like any other formula, without this component knowing what the mapping configures.
 *
 * A value keeps the type it was stored with. A number gets a number box and stays a number,
 * which is what lets a mapping written as JSON long ago come back unchanged after somebody
 * edited a neighbouring row; a new row is text, which is what a template value is.
 */
export function KeyValueField({ field, form, name, label, description, disabled }: KeyValueFieldProps) {
    const rows = keyValueRows(form.watch(name));
    // Stable identities for the rows, so removing the second one does not hand its editor's
    // state (an open list, the caret) to the third.
    const ids = useRef<number[]>([]);
    const next = useRef(0);
    while (ids.current.length < rows.length) ids.current.push(next.current++);
    if (ids.current.length > rows.length) ids.current = ids.current.slice(0, rows.length);

    const write = (updated: KeyValueRow[]) => form.setValue(name, updated, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    const update = (index: number, patch: Partial<KeyValueRow>) => write(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
    const remove = (index: number) => {
        ids.current = ids.current.filter((_, at) => at !== index);
        write(rows.filter((_, at) => at !== index));
    };
    const add = () => write([...rows, { key: "", value: "" }]);

    const { error } = form.getFieldState(name, form.formState);
    const arrayMessage = (error as { root?: { message?: string }; message?: string } | undefined)?.root?.message ?? error?.message;

    return (
        <div className="flex flex-col gap-1" data-testid={`key-value-${name}`}>
            {rows.length === 0 ? (
                <p className="py-1 text-xs text-gray-500">No entry yet.</p>
            ) : (
                // The name is short and the value is what gets read: a third of the row for
                // one, the rest for the other.
                <div className="grid grid-cols-[minmax(0,35fr)_minmax(0,65fr)_auto] items-start gap-x-1.5 gap-y-1.5">
                    <span className="text-[11px] text-gray-500">Name</span>
                    <span className="text-[11px] text-gray-500">Value</span>
                    <span />
                    {rows.map((row, index) => {
                        const keyError = form.getFieldState(`${name}.${index}.key`, form.formState).error?.message;
                        const rowId = `${name}.${index}`;
                        return (
                            <KeyValueRowInputs
                                key={ids.current[index]}
                                id={rowId}
                                row={row}
                                field={field}
                                label={label}
                                description={description}
                                disabled={disabled}
                                keyError={keyError}
                                onKey={key => update(index, { key })}
                                onValue={value => update(index, { value })}
                                onRemove={() => remove(index)}
                            />
                        );
                    })}
                </div>
            )}
            {!disabled && (
                <div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="-ml-2 text-blue-600 hover:text-blue-800"
                        aria-label={`Add ${label}`}
                        data-testid={`key-value-add-${name}`}
                        onClick={add}
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        Add
                    </Button>
                </div>
            )}
            {arrayMessage && <span className="text-xs text-red-500">{arrayMessage}</span>}
        </div>
    );
}

type RowProps = {
    id: string;
    row: KeyValueRow;
    field: FieldDataType;
    label: string;
    description?: string;
    disabled?: boolean;
    keyError?: string;
    onKey: (key: string) => void;
    onValue: (value: unknown) => void;
    onRemove: () => void;
};

function KeyValueRowInputs({ id, row, field, label, description, disabled, keyError, onKey, onValue, onRemove }: RowProps) {
    return (
        <>
            <div className="flex flex-col gap-0.5">
                <Input
                    id={`${id}.key`}
                    aria-label={`${label}: name of row ${row.key || "new"}`}
                    value={row.key}
                    disabled={disabled}
                    spellCheck={false}
                    placeholder="name"
                    onChange={event => onKey(event.target.value)}
                    className={cn(ROW_INPUT, "font-mono text-[12px]", keyError && "border-red-400")}
                />
                {keyError && <span className="text-[11px] leading-snug text-red-600">{keyError}</span>}
            </div>
            <div className="min-w-0">
                <ValueInput id={`${id}.value`} row={row} field={field} label={label} description={description} disabled={disabled} onValue={onValue} />
            </div>
            {disabled ? (
                <span />
            ) : (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-6 text-gray-400 hover:text-red-700"
                    aria-label={`Remove ${row.key || "this row"}`}
                    onClick={onRemove}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            )}
        </>
    );
}

function ValueInput({ id, row, field, label, description, disabled, onValue }: Omit<RowProps, "keyError" | "onKey" | "onRemove">) {
    const { value } = row;

    if (typeof value === "number") {
        return (
            <Input
                id={id}
                type="number"
                aria-label={`${label}: ${row.key}`}
                value={Number.isFinite(value) ? value : ""}
                disabled={disabled}
                onChange={event => onValue(event.target.value === "" ? "" : Number(event.target.value))}
                className={ROW_INPUT}
                title="A number, sent as a number."
            />
        );
    }

    if (value !== null && value !== undefined && typeof value !== "string") {
        // A boolean or a nested structure somebody stored as JSON: shown and editable as
        // JSON, and written back as the value it parses to.
        return (
            <Input
                id={id}
                aria-label={`${label}: ${row.key}`}
                value={JSON.stringify(value)}
                disabled={disabled}
                spellCheck={false}
                onChange={event => onValue(parseOrKeep(event.target.value))}
                className={cn(ROW_INPUT, "font-mono text-[12px]")}
            />
        );
    }

    const text = value ?? "";
    if (field.formula) {
        return (
            <FormulaField
                id={id}
                formula={field.formula}
                value={text}
                disabled={disabled}
                compact
                label={row.key ? `${label} · ${row.key}` : label}
                description={description}
                onChange={onValue}
            />
        );
    }
    return <Input id={id} aria-label={`${label}: ${row.key}`} value={text} disabled={disabled} onChange={event => onValue(event.target.value)} className={ROW_INPUT} />;
}

function parseOrKeep(text: string): unknown {
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}
